/**
 * PostHog Analytics Integration
 *
 * Provides funnel analysis, A/B testing (feature flags), and session replay.
 * Free tier: 1M events/month, 5K sessions, unlimited funnels.
 *
 * Usage:
 *   import * as posthog from './posthog';
 *
 *   posthog.init();
 *   posthog.identify(userId, { email, org_id, ... });
 *   posthog.track('event_name', { param: 'value' });
 *   const variant = posthog.getFeatureFlag('experiment-name');
 */

import type { CaptureResult } from 'posthog-js';
import type { AnalyticsUser } from './types';
import { POSTHOG_API_KEY } from './config';
import { preserveTrackingParams } from './trackingParams';
import { getFirstLandingPage } from './firstTouch';
import { isModernBrowser } from '@/lib/browserCompat';

const IS_PRODUCTION = import.meta.env.PROD;
const DEBUG_MODE =
  typeof window !== 'undefined' && localStorage.getItem('analytics_debug') === 'true';

let initialized = false;

/**
 * posthog-js is loaded on demand, NOT statically imported.
 *
 * A static `import posthogLib from 'posthog-js'` put the whole SDK in the ENTRY
 * chunk, because App.tsx statically imports @/lib/analytics which imports this
 * module. Measured from the deployed bundle's sourcemap: 227kB of the entry
 * chunk's 397kB parsed - 57% of it - or 199kB raw / 65kB gzip standalone. Every
 * page, including anonymous marketing traffic, downloaded and parsed it before
 * React could render.
 *
 * Nothing needs it synchronously. init() already runs from an effect
 * (App.tsx), and the two feature-flag consumers go through useFeatureFlag,
 * which is built around flags arriving late.
 */
type PostHogLib = typeof import('posthog-js').default;

let posthogLib: PostHogLib | null = null;

/** Set when init() decides PostHog will never run (no key, dev, legacy browser). */
let disabled = false;

/**
 * Resolves once posthogLib.init() has actually run.
 *
 * Every fire-and-forget call chains on this rather than checking a boolean, so
 * events raised while the chunk is still downloading are QUEUED instead of
 * dropped - the old `if (!initialized) return;` guard would have silently lost
 * them, and the first $pageview fires immediately on mount. Callbacks attached
 * to one promise run in registration order, so ordering is preserved and
 * init() (registered first, by definition) always runs before any capture.
 */
let markReady: (lib: PostHogLib) => void;
const ready = new Promise<PostHogLib>((resolve) => {
  markReady = resolve;
});

/** Queue work until PostHog is live. Never runs if init() bailed or was never called. */
function whenReady(fn: (lib: PostHogLib) => void): void {
  if (disabled) return;
  void ready.then(fn);
}

// Browser-extension-injected errors that aren't ours. PostHog's
// capture_exceptions ingests them as $exception events (Sentry already drops
// these via ignoreErrors), polluting the error stream and masking real issues.
const EXTENSION_NOISE_PATTERNS: RegExp[] = [
  /Object Not Found Matching Id/i,
  /Non-Error promise rejection captured with value/i,
];

/** True if a captured $exception is browser-extension noise we should drop. */
export function isExtensionNoiseException(properties?: Record<string, unknown>): boolean {
  if (!properties) return false;
  const parts: string[] = [];
  const list = properties.$exception_list;
  if (Array.isArray(list)) {
    for (const ex of list) {
      if (ex && typeof ex === 'object') {
        const value = (ex as Record<string, unknown>).value;
        const type = (ex as Record<string, unknown>).type;
        if (typeof value === 'string') parts.push(value);
        if (typeof type === 'string') parts.push(type);
      }
    }
  }
  if (typeof properties.$exception_message === 'string') parts.push(properties.$exception_message);
  if (typeof properties.$exception_values === 'string') parts.push(properties.$exception_values);
  const message = parts.join(' ');
  return message.length > 0 && EXTENSION_NOISE_PATTERNS.some((re) => re.test(message));
}

// Public content routes where session replay is not worth its cost. Replay
// instruments every interaction, and on guides and tools there is no funnel to
// analyse. It is a suspect for the mobile INP p75 of 301ms that Search Console
// flags across 70 of these URLs - but only a suspect: that figure has never been
// reproduced locally (worst synthetic interaction 64ms). Conversion routes
// (/pricing, landing pages) and authenticated app routes keep replay.
//
// Scope, verified in a browser against posthog-js 1.382.0: this suppresses
// EXACTLY ONE script, posthog-recorder.js. surveys.js,
// dead-clicks-autocapture.js, exception-autocapture.js and web-vitals.js are
// governed by separate config and still load here, so do not expect the whole
// PostHog payload to disappear from these pages.
const REPLAY_DISABLED_PATH_PREFIXES = ['/guides/', '/tools/'];

/**
 * Whether session replay should be suppressed for the current path.
 *
 * Matches on a trailing-slash prefix so `/features/guides-overview` is unaffected;
 * the collection pages `/guides` and `/tools` themselves are short and keep replay.
 */
function shouldDisableSessionRecording(pathname?: string): boolean {
  if (pathname == null && typeof window === 'undefined') return false;
  const path = pathname ?? window.location.pathname;
  return REPLAY_DISABLED_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

// Mirrors what we last told PostHog, so SPA navigation can flip recording without
// re-asserting it on every pageview.
let recordingSuppressed = false;

/**
 * Re-evaluate replay for the current route.
 *
 * init() only ever sees the landing path, so without this a visitor who lands on
 * a guide would have replay off for the whole session — including a later
 * /pricing visit, which is exactly the funnel replay is kept for.
 */
function syncSessionRecordingToRoute(pathname?: string): void {
  const shouldSuppress = shouldDisableSessionRecording(pathname);
  if (shouldSuppress === recordingSuppressed) return;

  recordingSuppressed = shouldSuppress;
  whenReady((lib) => {
    if (shouldSuppress) {
      lib.stopSessionRecording();
    } else {
      lib.startSessionRecording();
    }
  });
}

/**
 * Initialize PostHog
 * Call once on app load
 */
export async function init(): Promise<void> {
  if (initialized || disabled) return;

  // Skip if no API key or not in production (unless debug mode)
  if (!POSTHOG_API_KEY || (!IS_PRODUCTION && !DEBUG_MODE)) {
    disabled = true;
    return;
  }

  // Skip on legacy/bot browsers below our modern-JS baseline. PostHog lazily
  // loads a web-vitals extension that calls Array.prototype.at(); on ancient
  // engines (e.g. scrapers pinning Chrome 79) that throws an unhandled rejection
  // (Sentry CODEPULSE-BACKEND-C4). Skipping init also keeps bot traffic out of
  // our free-tier event/session-replay quota. Leaving `initialized` false makes
  // identify()/track()/page() no-op for these clients.
  if (!isModernBrowser()) {
    disabled = true;
    return;
  }

  // Seed the mirror from the landing route so syncSessionRecordingToRoute() only
  // acts when a later navigation actually changes the answer.
  recordingSuppressed = shouldDisableSessionRecording();

  // Deliberately AFTER every bail-out above, so clients that will never use
  // PostHog (dev, no key, legacy/bot engines) never pay for the download.
  try {
    posthogLib = (await import('posthog-js')).default;
  } catch {
    // Chunk failed to load (offline, blocked by an extension). Analytics is
    // optional; make the no-op permanent rather than queueing forever.
    disabled = true;
    return;
  }

  posthogLib.init(POSTHOG_API_KEY, {
    api_host: 'https://us.i.posthog.com',
    capture_pageview: false, // Manual SPA tracking via analytics.page()
    capture_pageleave: true, // Emit $pageleave for true exit pages + time-on-page
    capture_exceptions: true, // PostHog Error Tracking; correlates errors with session replay + funnel (additive to Sentry)
    persistence: 'localStorage',
    // On everywhere. It was off for GA4 event-catalogue parity, which cost us
    // retroactive click analysis and heatmaps, and left no generic "a human
    // clicked" event at all - the gap that made engaged_session necessary.
    // Volume is not a constraint: 24,952 events per 30 days against a 1M/month
    // free tier. Autocapture would otherwise send each clicked element's text and
    // attributes ($el_text, aria-labels, titles), which on signed-in routes are
    // developer names, PR titles and repo names. mask_all_text/_element_attributes
    // keep the click (and the "a human was here" signal) but drop the words, so
    // PostHog receives no on-screen customer data - what DPA Annex III promises.
    autocapture: true,
    mask_all_text: true,
    mask_all_element_attributes: true,
    disable_session_recording: recordingSuppressed,
    // Replays record the DOM of signed-in dashboards, which render customer
    // personal data (developer names, PR titles, review comments). Mask every
    // text node and every input so a replay shows layout and interaction only -
    // the DPA and privacy notice promise exactly this. Pinned by posthog.test.ts.
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
    },
    before_send: (captureResult: CaptureResult | null): CaptureResult | null => {
      if (!captureResult) return captureResult;
      // Drop browser-extension-injected exceptions (not our code).
      if (
        captureResult.event === '$exception' &&
        isExtensionNoiseException(captureResult.properties)
      ) {
        return null;
      }
      return captureResult;
    },
    loaded: () => {
      initialized = true;
    },
  });

  initialized = true;
  markReady(posthogLib);
}

/**
 * Identify user with properties
 * Call after login
 */
export function identify(user: AnalyticsUser): void {
  whenReady((lib) => lib.identify(user.userId, {
    name: user.name,
    email: user.email,
    github_login: user.githubLogin,
    org_id: user.orgId,
    org_slug: user.orgSlug,
    org_name: user.orgName,
    is_personal: user.isPersonal,
    subscription_tier: user.subscriptionTier,
    is_trial: user.isTrial,
    trial_days_remaining: user.trialDaysRemaining,
    signup_date: user.signupDate,
    first_landing_page: getFirstLandingPage() || undefined,
    logged_in: true,
  }));
}

/**
 * Track custom event
 */
export function track(
  eventName: string,
  properties?: Record<string, string | number | boolean | undefined>
): void {
  whenReady((lib) => lib.capture(eventName, properties));
}

/**
 * Track page view
 */
export function page(path: string, title: string): void {
  syncSessionRecordingToRoute(path);

  // $current_url is resolved NOW, not inside whenReady: by the time the chunk
  // lands the SPA may already have navigated, which would attribute the
  // pageview to the wrong URL.
  const currentUrl =
    window.location.origin + path + preserveTrackingParams(window.location.search);

  whenReady((lib) =>
    lib.capture('$pageview', {
      // Preserve ad/marketing attribution (utm_*, gclid, …) on the pageview URL so
      // URL-based funnels/breakdowns can attribute traffic. Sensitive params
      // (OAuth code/state, installation_id, token) are stripped by the allowlist.
      $current_url: currentUrl,
      title,
    })
  );
}

/**
 * Reset user data (on logout)
 */
export function reset(): void {
  whenReady((lib) => lib.reset());
}

/**
 * Get feature flag value for A/B testing
 *
 * Usage:
 *   const variant = posthog.getFeatureFlag('onboarding-simplified');
 *   if (variant === 'test') {
 *     // Show test variant
 *   }
 *
 * @returns string | boolean | undefined
 */
export function getFeatureFlag(flagKey: string): string | boolean | undefined {
  // Stays SYNCHRONOUS - callers read it during render. Before the chunk lands
  // it returns undefined, which is the same answer it already gave while
  // PostHog was fetching flags over the network. useFeatureFlag is built for
  // exactly that: it treats undefined as "not loaded yet" and re-reads from an
  // onFeatureFlags subscription, with a 3s timeout fallback.
  return posthogLib?.getFeatureFlag(flagKey);
}

/**
 * Check if a feature flag is enabled (boolean flags)
 */
export function isFeatureEnabled(flagKey: string): boolean {
  return posthogLib?.isFeatureEnabled(flagKey) ?? false;
}

/**
 * Register super properties (added to all events)
 * Useful for adding context like subscription_tier
 */
export function register(properties: Record<string, unknown>): void {
  whenReady((lib) => lib.register(properties));
}

/**
 * Manually reload feature flags (useful after user changes)
 */
export function reloadFeatureFlags(): void {
  whenReady((lib) => lib.reloadFeatureFlags());
}

/**
 * Subscribe to PostHog's feature-flag callback once the SDK is live.
 *
 * Replaces the old `export { posthogLib }`, which handed callers the module
 * object and therefore required it to be statically imported. Returns an
 * unsubscribe function that is safe to call before the SDK has loaded.
 */
export function onFeatureFlags(callback: () => void): () => void {
  let cancelled = false;
  let unsubscribe: (() => void) | undefined;

  whenReady((lib) => {
    if (cancelled) return;
    const result = lib.onFeatureFlags(callback);
    if (typeof result === 'function') unsubscribe = result;
  });

  return () => {
    cancelled = true;
    unsubscribe?.();
  };
}
